/**
 * Forms Status Component
 *
 * Displays the status of forms (missing, in progress, completed) for a timestep.
 */

import React, { useState } from "react";
import {
  Card,
  Text,
  Stack,
  Badge,
  Group,
  Accordion,
  Collapse,
  ActionIcon,
} from "@mantine/core";
import { IconChevronDown, IconChevronRight } from "@tabler/icons-react";
import type { FormInfo } from "../../types";

interface FormsStatusProps {
  missingForms: FormInfo[];
  inProgressForms: FormInfo[];
  completedForms: FormInfo[];
  incompleteForms?: FormInfo[]; // TaxWise only
}

export const FormsStatus: React.FC<FormsStatusProps> = ({
  missingForms,
  inProgressForms,
  completedForms,
  incompleteForms = [],
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const renderFormList = (forms: FormInfo[], color: string) => {
    if (forms.length === 0) {
      return (
        <Text size="sm" c="dimmed" fs="italic">
          None
        </Text>
      );
    }

    return (
      <Stack gap="xs">
        {forms.map((form, index) => (
          <Badge key={index} variant="light" color={color}>
            {typeof form === "string" ? form : form.name}
          </Badge>
        ))}
      </Stack>
    );
  };

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Stack gap="md">
        <Group
          justify="space-between"
          style={{ cursor: "pointer" }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div>
            <Group gap="sm" mb="xs">
              <Text size="xl" fw={600}>
                Forms Status
              </Text>
              <ActionIcon
                variant="subtle"
                size="sm"
                style={{ pointerEvents: "none" }}
              >
                {isExpanded ? (
                  <IconChevronDown size={20} />
                ) : (
                  <IconChevronRight size={20} />
                )}
              </ActionIcon>
            </Group>
            <Group gap="lg">
              <Text size="sm" c="dimmed">
                Missing: <strong>{missingForms.length}</strong>
              </Text>
              <Text size="sm" c="dimmed">
                In Progress: <strong>{inProgressForms.length}</strong>
              </Text>
              {incompleteForms && incompleteForms.length > 0 && (
                <Text size="sm" c="dimmed">
                  Incomplete: <strong>{incompleteForms.length}</strong>
                </Text>
              )}
              <Text size="sm" c="dimmed">
                Completed: <strong>{completedForms.length}</strong>
              </Text>
            </Group>
          </div>
        </Group>

        <Collapse in={isExpanded}>
          <Accordion defaultValue={null}>
            <Accordion.Item value="missing">
              <Accordion.Control>
                <Group>
                  <Text fw={600}>Missing Forms</Text>
                  <Badge color="red" variant="light">
                    {missingForms.length}
                  </Badge>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                {renderFormList(missingForms, "red")}
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="inProgress">
              <Accordion.Control>
                <Group>
                  <Text fw={600}>In Progress Forms</Text>
                  <Badge color="blue" variant="light">
                    {inProgressForms.length}
                  </Badge>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                {renderFormList(inProgressForms, "blue")}
              </Accordion.Panel>
            </Accordion.Item>

            {incompleteForms && incompleteForms.length > 0 && (
              <Accordion.Item value="incomplete">
                <Accordion.Control>
                  <Group>
                    <Text fw={600}>Incomplete Forms</Text>
                    <Badge color="yellow" variant="light">
                      {incompleteForms.length}
                    </Badge>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  {renderFormList(incompleteForms, "yellow")}
                </Accordion.Panel>
              </Accordion.Item>
            )}

            <Accordion.Item value="completed">
              <Accordion.Control>
                <Group>
                  <Text fw={600}>Completed Forms</Text>
                  <Badge color="green" variant="light">
                    {completedForms.length}
                  </Badge>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                {renderFormList(completedForms, "green")}
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </Collapse>
      </Stack>
    </Card>
  );
};
